import React, { forwardRef } from 'react';
import { color, space, font, radius, duration, easing } from './tokens';

// ---------------------------------------------------------------------------
// Button — Wave 4.1.A
//
// Variants: primary | secondary | destructive | ghost | link
// Sizes:    sm | md | lg
// States:   disabled, loading (forces aria-busy + spinner)
//
// forwardRef so RHF and Radix can attach refs without ceremony.
// ---------------------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'destructive' | 'ghost' | 'link';
type Size    = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:  Variant;
  size?:     Size;
  loading?:  boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
}

const variantStyles: Record<Variant, React.CSSProperties> = {
  primary: {
    background:  color.brand.primary,
    color:       color.text.inverse,
    border:      'none',
  },
  secondary: {
    background:  color.bg.card,
    color:       color.text.secondary,
    border:      `1px solid ${color.border.strong}`,
  },
  destructive: {
    background:  color.status.error.bg,
    color:       color.status.error.text,
    border:      `1px solid ${color.status.error.border}`,
  },
  ghost: {
    background:  'transparent',
    color:       color.text.secondary,
    border:      'none',
  },
  link: {
    background:  'transparent',
    color:       color.text.link,
    border:      'none',
    padding:     0,
    textDecoration: 'underline',
  },
};

const sizeStyles: Record<Size, React.CSSProperties> = {
  sm: {
    padding:    `${space[1]}px ${space[3]}px`,
    fontSize:   font.size.sm,
    minHeight:  32,
  },
  md: {
    padding:    `${space[2]}px ${space[4]}px`,
    fontSize:   font.size.base,
    minHeight:  40,
  },
  lg: {
    padding:    `${space[3]}px ${space[5]}px`,
    fontSize:   font.size.lg,
    minHeight:  48,
  },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant   = 'primary',
    size      = 'md',
    loading   = false,
    iconLeft,
    iconRight,
    fullWidth = false,
    disabled,
    children,
    style,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  const v = variantStyles[variant];
  const s = sizeStyles[size];

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading}
      data-ds-button=""
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        justifyContent: 'center',
        gap:            space[2],
        borderRadius:   radius.md,
        fontWeight:     font.weight.semibold,
        cursor:         isDisabled ? 'not-allowed' : 'pointer',
        opacity:        isDisabled ? 0.6 : 1,
        transition:     `background ${duration.fast}ms ${easing.standard}, filter ${duration.fast}ms ${easing.standard}, transform ${duration.fast}ms ${easing.standard}`,
        userSelect:     'none',
        width:          fullWidth ? '100%' : 'auto',
        ...v,
        ...s,
        ...(variant === 'link' ? { padding: 0, minHeight: 0 } : {}),
        ...style,
      }}
      {...rest}
    >
      {loading ? <Spinner size={size === 'lg' ? 16 : 14} /> : iconLeft}
      {children && <span>{children}</span>}
      {!loading && iconRight}
    </button>
  );
});

const Spinner: React.FC<{ size: number }> = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ animation: 'ds-spin 0.8s linear infinite' }}
  >
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
    <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export default Button;
